import { Form, useFormikContext } from 'formik';
import { Alert, Button, Tab, Tabs } from 'react-bootstrap';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import spinnerTitle from 'components/others/spinnerTitle';
import SolarIcon from 'components/icons/SolarIcon';
import SvgSave from 'components/svg/SvgSave';
import TabGeneral from './tabs/TabGeneral';
import TabFiles from './tabs/TabFiles';
import TabHistory from './tabs/TabHistory';
import TabService from './tabs/TabService';
import type useWarrantyCard from './model/useWarrantyCard';
import type { WarrantyFormValues } from './model/warrantyDates';

interface WarrantyFormProps {
    card: ReturnType<typeof useWarrantyCard>;
    canEdit: boolean;
}

const WarrantyForm = ({ card, canEdit }: WarrantyFormProps) => {
    const { t } = useTranslation(['Warranties', 'common']);
    const [tab, setTab] = useState('general');
    const form = useFormikContext<WarrantyFormValues>();
    const editable = canEdit && !card.row?.archived;
    const saved = card.id > 0;
    const hasSerial = saved
        ? Boolean(form.values.serialNumber.trim())
        : form.values.serialNumbers.some((serial) => serial.trim());
    return (
        <Form className="d-flex flex-column h-100">
            <div className="off-product-card-tabs d-flex flex-column h-100">
                <div className="off-product-card-tabs-content px-3">
                    {card.error && <Alert variant="danger">{card.error}</Alert>}
                    <Tabs
                        variant="underline"
                        activeKey={tab}
                        onSelect={(key) => setTab(key || 'general')}
                        className="mb-3 invoice-tab border-bottom"
                    >
                        <Tab
                            eventKey="general"
                            title={
                                <>
                                    <SolarIcon name="gear" className="me-2 fs-14" />
                                    {t('tabs.general')}
                                </>
                            }
                        >
                            <TabGeneral row={card.row} disabled={!editable || card.busy} />
                        </Tab>
                        {saved && (
                            <Tab
                                eventKey="files"
                                title={
                                    <>
                                        <SolarIcon name="paperclip" className="me-2 fs-14" />
                                        {t('tabs.files')}
                                    </>
                                }
                            >
                                <TabFiles
                                    id={card.id}
                                    files={card.row?.files || '[]'}
                                    canEdit={canEdit}
                                    save={card.save}
                                    saveFiles={card.saveFiles}
                                />
                            </Tab>
                        )}
                        {saved && (
                            <Tab
                                eventKey="history"
                                title={
                                    <>
                                        <SolarIcon name="clock-history" className="me-2 fs-14" />
                                        {t('tabs.history')}
                                    </>
                                }
                            >
                                <TabHistory rows={card.history} />
                            </Tab>
                        )}
                        {saved && (
                            <Tab
                                eventKey="service"
                                title={
                                    <>
                                        <SolarIcon name="toolbox" className="me-2 fs-14" />
                                        {t('tabs.service')}
                                    </>
                                }
                            >
                                <TabService
                                    rows={card.service}
                                    canEdit={canEdit && card.id > 0}
                                    busy={card.busy}
                                    onAdd={card.addService}
                                />
                            </Tab>
                        )}
                    </Tabs>
                </div>
                {tab === 'general' && editable && (
                    <div className="p-3 offcanvas-footer off-product-card-tabs-footer">
                        <div className="d-flex justify-content-end gap-2">
                            <Button
                                type="submit"
                                variant="info"
                                className="min100px"
                                disabled={
                                    card.busy ||
                                    !form.values.productId ||
                                    !hasSerial ||
                                    !form.values.purchasedAt ||
                                    !form.values.warrantyUntil
                                }
                            >
                                {spinnerTitle(
                                    <>
                                        <SvgSave className="me-2 mtop-2" />
                                        {t('save')}
                                    </>,
                                    card.busy,
                                    t('common:buttons.saving'),
                                )}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Form>
    );
};

export default WarrantyForm;
